import { ResultMessageType } from "@multiweb/types/contracts";
import { EOWValidateException } from "OW.Remoting/Basic/OWExceptions";
import { DeviceInfo } from "../../../../Device/Info/DeviceInfo";
import { IValidationDataObject } from "../../../../OW.Remoting/Remoting/DataTypes/IValidationDataObject";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { DataRow } from "../../../UI.Controls/DataGridSubcontrols/Components/DataRow";
import { SaveResultData } from "../../../UI.Controls/DataSource/EditableDBDataSource";
import { IUserMsgItem, IUserMsgsData, UserMsgsBuilder } from "../DataBuilders/UserMsgsBuilder";
import { MessageButtonType, MessageWindow } from "../MessageWindow";
import { IValidatableMasterableFrame } from "./Frames/FrameInterfaces";
import { IValidatableComponentData, IValidationData, IValidationResult, ValidationManager } from "./ValidationManager";
import { IValidationError } from "OW.Remoting/Basic/EOWExceptionsInterfaces";
import { MessageButtonResult } from "../UIManager";

export class SharedMethods {
    public static horizontalLayout = DeviceInfo.deviceProperties.tablet;

    /**
     * Zpracování zpráv při ukladaní záznamů
     * @param resultMessage - zpravy ze sluzby pri ukladani zaznamu
     */
    public static async processSaveResultsAndShowMessages(frame: IValidatableMasterableFrame, resultMessage: SaveResultData[]): Promise<SaveResultData[]> {
        const saveMessages: IUserMsgsData[] = [];
        const errorMessages: SaveResultData[] = [];

        const getRowFromMessage = (saveResultMessage: SaveResultData): DataRow | null => {
            if (!saveResultMessage.data)
                return null;

            const objectInstanceId = saveResultMessage.data.instanceID;
            let row = frame.element.querySelector(`.DataRow[datakey='${objectInstanceId}']`); //`
            if (!row || !(row instanceof DataRow)) {
                const objectId = saveResultMessage.data.id;
                if (objectId == null)
                    return null;
                row = frame.element.querySelector(`.DataRow[datakey='${objectId}']`); //`
            }

            if (!row || !(row instanceof DataRow))
                return null;

            return row;
        };

        if (resultMessage !== undefined)
            for (const aktMessage of resultMessage) {
                //naplneni validationManageru
                if (aktMessage.errors) {
                    const row = getRowFromMessage(aktMessage);

                    for (const e of aktMessage.errors) {
                        const componentsData: IValidatableComponentData[] = [];

                        if (e.attributeNames != null && row) {
                            for (const attrName of e.attributeNames) {
                                const attrNameUpper = attrName.toUpperCase();
                                if (row && (row instanceof DataRow)) {
                                    for (const cell of row.dataCells) {
                                        if (cell.column.attrName.toUpperCase() !== attrNameUpper)
                                            continue;

                                        componentsData.push({ control: cell, container: row, attributeName: attrName, showsInvalidData: () => true });
                                    }
                                }
                            }
                        }

                        frame.validationManager.addDataError(aktMessage.data as any as IValidationDataObject<IValidationData>, e, componentsData);
                    }
                    errorMessages.push(aktMessage);
                } else {
                    if (aktMessage.messages.length > 0) {
                        const messageItems: IUserMsgItem[] = [];
                        let error: boolean = false;
                        for (const message of aktMessage.messages) {
                            const msg: IUserMsgItem = { type: message.type, message: message.text };

                            if (message.type === ResultMessageType.error) {
                                error = true;
                                msg.additionalParams = [message.locKey]; // u errorů se má zobrazovat i kód chyby
                            }

                            messageItems.push(msg);
                        }

                        saveMessages.push({ objectIdentification: aktMessage?.data?.objectIdentity, messages: messageItems });

                        if (error) {
                            errorMessages.push(aktMessage);

                            const row = getRowFromMessage(aktMessage);
                            if (!row)
                                continue;

                            frame.validationManager.addRowError(aktMessage.data.id, row);
                        }
                    }
                }
            }

        if (saveMessages.length > 0) {
            new MessageWindow(new UserMsgsBuilder(saveMessages)).show();
        }

        return errorMessages;
    }

    /**
     * Zkontroluje zprávy z validačního managera a na jejich základě zobrazí specifický dialog {@link MessageWindow} čekající na akci od uživatele.
     * @param validationManager
     * @param runLocKey lokalizace pro tlačítko yes v yes/no dialogu
     * @param cancelLocKey lokalizace pro tlačítko no v yes/no dialogu
     * @param serverValidationOnly Propustí pouze validační chyby ze serveru
     * @return Vrací výsledek kontroly za základě typu validačních zpráv/akce od uživatele.
     */
    public static async checkObjectBeforeExecute(validationManager: ValidationManager, runLocKey: string, cancelLocKey: string, serverValidationOnly?: boolean): Promise<ObjectCheckingResult> {
        const validationManagerMessages = validationManager.getErrorMessages(serverValidationOnly);

        return await this.checkMessagesBeforeExecute(validationManagerMessages, runLocKey, cancelLocKey);
    }

    /**
     * Zkontroluje validační zprávy a na jejich základě zobrazí specifický dialog {@link MessageWindow} čekající na akci od uživatele.
     * @param validationMessages validační zprávy, typicky se zde využívají zprávy z validationManagera
     * @param runLocKey lokalizace pro tlačítko yes v yes/no dialogu
     * @param cancelLocKey lokalizace pro tlačítko no v yes/no dialogu
     * @return Vrací výsledek kontroly za základě typu validačních zpráv/akce od uživatele.
     */
    public static async checkMessagesBeforeExecute(validationMessages: IValidationResult[], runLocKey: string, cancelLocKey: string): Promise<ObjectCheckingResult> {
        let hasError: boolean = false;
        const saveMessages: IUserMsgsData[] = [];

        if (!validationMessages) {
            return ObjectCheckingResult.clear;
        }

        for (const errorMessage of validationMessages) {
            if (errorMessage.messages.length === 0) {
                continue;
            }

            const newMessage = { objectIdentification: (errorMessage.data as OWObject).objectIdentity, ID: (errorMessage.data as OWObject).id, messages: errorMessage.messages };
            saveMessages.push(newMessage);
            if (!hasError) {
                for (const msg of errorMessage.messages) {
                    if (msg.type === ResultMessageType.error) {
                        hasError = true;
                        break;
                    }
                }
            }
        }

        if (saveMessages.length > 0) {
            if (hasError) {
                const mw = new MessageWindow(new UserMsgsBuilder(saveMessages));
                await mw.show();
                return ObjectCheckingResult.error;
            } else {
                const r = await MessageWindow.showYesNoDialog(new UserMsgsBuilder(saveMessages), runLocKey, cancelLocKey); //"GUI-6"=Uložit,"GUI-33412"=Zrušit
                switch (r.result) {
                    case MessageButtonType.ok:
                        return ObjectCheckingResult.recommendedContinue;
                    case MessageButtonType.no:
                        return ObjectCheckingResult.recommendedStop;
                }
            }
        }
        return ObjectCheckingResult.clear;
    }

    /**
     * Zobrazí validační chyby uživateli, pokud nějaké existují
     * @param validationManager Validační manažer, který obsahuje případné chyby
     */
    public static async showValidationMessages(validationManager: ValidationManager): Promise<void> {
        const validationManagerMessages = validationManager.getErrorMessages();

        const validMsgs: IUserMsgsData[] = validationManagerMessages.map((item) => {
            return { objectIdentification: (item.data as OWObject).objectIdentity, ID: (item.data as OWObject).id, messages: item.messages };
        });

        if (validMsgs.length > 0) {
            await new MessageWindow(new UserMsgsBuilder(validMsgs)).show();
        }
    }

    /**
     * Metoda, která znevalidní záznamy, zobrazí chybovou hlášku a následně odstraní validační chyby
     * @param objects Objekty na kterých byl zjistěn error
     * @param error Chyba pro zobrazení hlášky
     * @param frame Frame na kterém jsou umístěny záznamy a validační manager
     */
    public static async handleValidationErrors(objects: OWObject[], error: EOWValidateException, frame: IValidatableMasterableFrame): Promise<void> {
        const saveResultData: SaveResultData[] = [];
        const validationMessages: IUserMsgsData[] = [];
        const objectsIDs: string[] = [];

        for (const object of objects) {
            saveResultData.push({ data: object, errors: error.validationErrors });
            validationMessages.push({
                objectIdentification: object.objectIdentity,
                messages: [{
                    message: error.validationErrors[0]?.errorMessage,
                    type: ResultMessageType.error,
                }],
            });
            objectsIDs.push(object.id);
        }

        await this.processSaveResultsAndShowMessages(frame, saveResultData); //znevalidní záznamy, ale nezobrazí hlášku
        const mw = new MessageWindow(new UserMsgsBuilder(validationMessages));
        await mw.show();

        frame.validationManager.clearMultipleErrors(objectsIDs);
    }

    /**
     * Zobrazí validační zprávy jako dialog {@link MessageWindow} čekající na akci od uživatele.
     * @param validationErrors Validační chyby
     * @param obj Objekt, ke kterému validační chyby patří
     */
    public static async showValidationErrors(validationErrors: IValidationError[], obj?: OWObject): Promise<MessageButtonResult | null> {
        if (validationErrors?.length) {
            const message: IUserMsgsData = {
                objectIdentification: obj?.id,
                messages: validationErrors.map<IUserMsgItem>(error => ({
                    type: ResultMessageType.error,
                    message: error.errorMessage,
                })),
            };
            return await MessageWindow.showAlert(new UserMsgsBuilder([message]));
        }
        return null;
    }
}

export enum ObjectCheckingResult {
    error = 0, //Validační chyba (např. nevyplněné requrired nebo postponed pole)
    recommendedContinue = 1, //Není vyplněné recommended pole, ale uživatel potvrdil akci
    recommendedStop = 2, //Není vyplněné recommended pole a uživatel přerušil vykonávání akce kliknutím na zrušit
    clear = 3, //záznam je validní nez validační chyby
}
